import React from 'react';
import styled from 'styled-components';
import useTranslation from '@src/hooks/useTranslation';

import useCurrentCourse from '@src/hooks/useCurrentCourse';
import { isPublished } from '@src/utils/course';
import { isExpired, getDiffDay } from '@src/utils/time';
import { STATE_SUCCESS } from '@src/const/common';

import { TimeIcon, GuaranteeIcon } from '@src/components/common/icon';

const CourseStatusWrapper = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	width: 100%;
	margin-top: 16px;
`;

const Status = styled.div`
	font-size: 14px;
	color: ${props => (isPublished(props.status) ? props.theme.colors.primary : props.theme.colors.secondary)};
`;

const InfoBlock = styled.div`
	display: flex;
	align-items: center;

	&:before{
		content:'|';
		margin: 0 12px;
    color: #dddddd;
	}
`;

const InfoTitle = styled.span`
	margin: 0 8px;
	font-size: 12px;

	@media ${props => props.theme.device.desktop} {
		margin-left: 0;
	}

	@media ${props => props.theme.device.mobile} {
		margin-left: 0;
	}
`;

const InfoContent = styled.span`
	font-size: 14px;
	font-weight: 700;
	color: #333333;
`;

const StyledTimeIcon = styled(TimeIcon)`
	@media ${props => props.theme.device.desktop} {
		display: none;
	}

	@media ${props => props.theme.device.mobile} {
		display: none;
	}
`;

const StyledGuaranteeIcon = styled(GuaranteeIcon)`
	@media ${props => props.theme.device.desktop} {
		display: none;
	}

	@media ${props => props.theme.device.mobile} {
		display: none;
	}
`;

const CourseStatus = (): JSX.Element => {
	const { t } = useTranslation();
	const {
		currentCourseState,
		currentCourse: { status, paid, is_free_course, discount_expired_at, refunded_days },
	} = useCurrentCourse();
	const showRefundedDay = !!refunded_days && isPublished(status) && !paid;

	if (currentCourseState !== STATE_SUCCESS) {
		return null;
	}
	return (
		<CourseStatusWrapper>
			<Status status={status}>{t(`course:status:${status}`)}</Status>
			{isPublished(status) && !is_free_course && (
				<>
					{discount_expired_at && !isExpired({ endTime: discount_expired_at }) && (
						<InfoBlock>
							<StyledTimeIcon />
							<InfoTitle>{t('course:discount_countdown:title')}</InfoTitle>
							<InfoContent>
								{t('course:discount_countdown:day', {
									day: getDiffDay({ endTime: discount_expired_at }),
								})}
							</InfoContent>
						</InfoBlock>
					)}
					{showRefundedDay && (
						<InfoBlock>
							<StyledGuaranteeIcon />
							<InfoTitle>{t('course:refund:title')}</InfoTitle>
							<InfoContent>{t('course:refund:day', { day: refunded_days })}</InfoContent>
						</InfoBlock>
					)}
				</>
			)}
		</CourseStatusWrapper>
	);
};

export default CourseStatus;
